/**
 * Datasheet grid — Access `frmMatchingPSMatch_Stock_Sales_dts`.
 *
 * Migrate sang template `dataList/` theo skill `table-style` (2026-05-25):
 *   - TableVirtuoso (virtualized, no 100-row cap)
 *   - FilterBar global Search (1 button, scan toàn bộ TABLE_COLUMNS)
 *   - Per-column filter ▽ icon trong header (text/checkboxes/date/number tự detect)
 *   - Clear All (N) duy nhất
 *   - CSS class `app-dataframe*` từ `src/styles/app-shared.css`
 * Read-only datasheet, single-row selection → user pick row → click Load → header hydrate.
 */
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { useGetStockSalesRowsQuery } from '@/store/api/matchingPsApi'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  STOCK_SALES_COLUMNS,
  type StockSalesColumnDef,
  type StockSalesDatasheetRow,
} from './stockSalesColumns'
import {
  formatAccessDate,
  formatAccessDateTime,
  formatStandard,
  toNumber,
} from './stockSalesUtils'
import { useMatchingFilterPref, type MatchingFilterCtx } from '../matchingFilterPrefs'
import { matchingRangeDateFieldSx } from '../matchingFilterUi'

interface Props {
  selectedMatchId: number | null
  onSelect: (matchId: number | null) => void
  /** Title rendered in the FilterBar (replaces the default "Filter" label). */
  title?: string
  /** Action buttons rendered in the FilterBar's rightSlot. */
  actions?: ReactNode
}

type FilterKind = 'search'

const ALL_ROWS_PAGE_SIZE = -1

/**
 * Access ConditionalFormat — datasheet `frmMatchingPSMatch_Stock_Sales_dts`.
 * Audit toàn bộ 22 cột: CHỈ 2 cột có format condition (20 cột còn lại không có):
 *   - `s_unallocated`     ("S - Unmatched (Warehouse)")
 *   - `stack_unallocated` ("Stack - Sales Unallocated")
 * Rule (decode binary): operator `acLessThan` (=5), Expression1 = "0" → highlight
 * khi value < 0 (over-allocated). ForeColor #ED1C24 (đỏ) trên BackColor #FFF200
 * (vàng) — giữ đúng màu Access (khác `*_plan_pending`: op acNotEqual + nền trắng).
 */
const HIGHLIGHT_WHEN_NEGATIVE: ReadonlySet<keyof StockSalesDatasheetRow> = new Set([
  's_unallocated',
  'stack_unallocated',
])

const cellValue = (row: StockSalesDatasheetRow, col: StockSalesColumnDef): string => {
  const v = row[col.key]
  if (v == null || v === '') return ''
  // Allocated ID = Access Long (không có Format) → integer, không thập phân/thousand-sep.
  if (col.key === 'match_id') return String(Math.round(toNumber(v)))
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'percent') {
    const n = toNumber(v)
    return `${(n * 100).toFixed(2)}%`
  }
  if (col.kind === 'number') {
    const n = toNumber(v)
    return n === 0 && (v === 0 || v === '0') ? '0.00' : formatStandard(v)
  }
  return String(v)
}

/** match_date của row ∈ [from, to] (YYYY-MM-DD; '' = không giới hạn đầu đó). */
const matchDateInRange = (
  v: StockSalesDatasheetRow['match_date'],
  from: string,
  to: string,
): boolean => {
  if (!from && !to) return true
  if (v == null || v === '') return false
  const d = dayjs(String(v))
  if (!d.isValid()) return false
  const day = d.format('YYYY-MM-DD')
  if (from && day < from) return false
  if (to && day > to) return false
  return true
}

export default function StockSalesDataGrid({ selectedMatchId, onSelect, title, actions }: Props) {
  // Date-range filter persist per-user qua user_preferences (skill user-logaction),
  // context 'stock-sales'. null/'' = KHÔNG lọc (hiện toàn bộ; không default fiscal).
  const filterPref = useMatchingFilterPref<MatchingFilterCtx>('stock-sales')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [hydrated, setHydrated] = useState(false)
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current || filterPref.isLoading) return
    hydratedRef.current = true
    setDateFrom(filterPref.value.dateFrom ?? '')
    setDateTo(filterPref.value.dateTo ?? '')
    setHydrated(true)
  }, [filterPref])

  // Query KHÔNG args → CÙNG cache key với StockSalesPage (RTK dedupe → CHỈ 1
  // request, tránh double-fetch view ~2.5s). Lọc ngày làm CLIENT-SIDE trên data
  // đã nhận (xem filteredRows) → đổi ngày KHÔNG gọi lại BE; view chỉ load 1 lần.
  const { data, isLoading, isFetching, error } = useGetStockSalesRowsQuery()

  const baseRows = useMemo<StockSalesDatasheetRow[]>(() => {
    if (!data) return []
    return data.map((r, i) => ({
      ...(r as StockSalesDatasheetRow),
      id: (r.match_id as number | string | undefined) ?? `row-${i}`,
    }))
  }, [data])

  /* ───────── Filter / sort / pagination state ───────── */
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] =
    useState<TableSortState<keyof StockSalesDatasheetRow> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) =>
    filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof StockSalesDatasheetRow
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  // Date-range lọc CLIENT-SIDE trên baseRows (không gọi lại BE) + persist delta
  // vào user row (patch auto-debounced trong usePreference). Đổi ngày = lọc tức thì.
  const handleDateFrom = (v: string) => {
    setDateFrom(v)
    setPage(0)
    void filterPref.patch({ dateFrom: v || null })
  }
  const handleDateTo = (v: string) => {
    setDateTo(v)
    setPage(0)
    void filterPref.patch({ dateTo: v || null })
  }

  const { getWidth, startResize } = useColumnWidths('matching.stock_sales.datasheet')

  const columnsCtrl = usePreferenceColumns<StockSalesColumnDef>({
    scope: 'matching-ps',
    prefKey: 'columns',
    subKey: 'stock-sales',
    allColumns: STOCK_SALES_COLUMNS,
    widthsTableKey: 'matching.stock_sales.datasheet',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  /* ───────── Apply 2-cấp filter ───────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          matchDateInRange(row.match_date, dateFrom, dateTo) &&
          rowMatchesSearch(row, searchTerm, STOCK_SALES_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm, dateFrom, dateTo],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, STOCK_SALES_COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  /* ───────── Popover meta khi mở col ▽ ───────── */
  const popoverColumn = useMemo<StockSalesColumnDef | null>(() => {
    if (!colPopover) return null
    return STOCK_SALES_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: keyof StockSalesDatasheetRow) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const minTableWidth = Math.max(
    960,
    visibleColumns.reduce((acc, c) => acc + (c.width ?? 120), 0),
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        px: 1,
        pt: 0.75,
        pb: 1,
        // Bảng FILL hết không gian flex còn lại (parent chain đã bounded) thay vì
        // clamp height theo viewport → bỏ khoảng trống dưới bảng. Override CSS clamp.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        showLabel={!title}
        rightSlot={actions}
      >
        {title && (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, flexShrink: 0, mr: 1, whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
        )}
        <DatePicker
          label="Date from"
          format="DD-MMM-YYYY"
          value={dateFrom ? dayjs(dateFrom) : null}
          onChange={(d) => handleDateFrom(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true },
              sx: matchingRangeDateFieldSx,
            },
          }}
        />
        <DatePicker
          label="to"
          format="DD-MMM-YYYY"
          value={dateTo ? dayjs(dateTo) : null}
          onChange={(d) => handleDateTo(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true },
              sx: matchingRangeDateFieldSx,
            },
          }}
        />
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => openFilterPopover('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
        >
          Columns
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load datasheet.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.68rem',
                },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': {
                  transform: 'scale(0.86)',
                  transformOrigin: 'center',
                },
              }}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPage(0)
              }}
              placeholder="Allocation, side, client, stack..."
              label="Search datasheet"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load datasheet"
              description="Please refresh the page or contact the system administrator."
            />
          ) : (isLoading || !hydrated) && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No allocation records"
              description="Use the form above to create the first allocation, or adjust filters."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(String(col.key)) ?? col.width
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          // Cho phép bôi đen + copy tên cột (click-sort vẫn chạy).
                          userSelect: 'text',
                          position: 'relative',
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(String(col.key))}
                          onClick={(position) =>
                            setColPopover({ key: col.key, position })
                          }
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle
                          onMouseDown={(e) => startResize(String(col.key), e)}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_, row: StockSalesDatasheetRow) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(String(col.key)) ?? col.width
                  const wrap = getWidth(String(col.key)) != null
                  const highlight =
                    HIGHLIGHT_WHEN_NEGATIVE.has(col.key) && toNumber(row[col.key]) < 0
                  if (wrap) {
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          py: 0.5,
                          // Access CF: value < 0 → ForeColor #ED1C24 / BackColor #FFF200.
                          ...(highlight && {
                            bgcolor: '#fff200',
                            color: '#ed1c24',
                            fontWeight: 700,
                          }),
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          verticalAlign: 'top',
                        }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'top',
                        // Access CF: value < 0 → ForeColor #ED1C24 / BackColor #FFF200.
                        ...(highlight && {
                          bgcolor: '#fff200',
                          color: '#ed1c24',
                          fontWeight: 700,
                        }),
                      }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{
                          display: 'inline-block',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                /* eslint-disable @typescript-eslint/no-explicit-any */
                Table: (props: any) => (
                  <Table
                    {...props}
                    size="small"
                    stickyHeader
                    sx={{ minWidth: minTableWidth, width: '100%' }}
                  />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowMatchId = Number.isFinite(idx)
                    ? Number(rows[idx]?.match_id ?? Number.NaN)
                    : Number.NaN
                  const isSelected =
                    Number.isFinite(rowMatchId) && rowMatchId === selectedMatchId
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (Number.isFinite(rowMatchId)) onSelect(rowMatchId)
                      }}
                    />
                  )
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
